import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faUser,
  faClock,
  faShieldAlt,
  faExclamationTriangle,
  faExclamationCircle,
  faEdit,
  faLink,
  faSearch,
  faNetworkWired,
  faStickyNote,
  faCalendarAlt,
  faBell,
  faFingerprint,
  faBullseye,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-100 dark:bg-red-800', text: 'text-red-800 dark:text-red-100', icon: faExclamationCircle },
  high: { bg: 'bg-orange-100 dark:bg-orange-800', text: 'text-orange-800 dark:text-orange-100', icon: faExclamationTriangle },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-800', text: 'text-yellow-800 dark:text-yellow-100', icon: faExclamationTriangle },
  low: { bg: 'bg-green-100 dark:bg-green-800', text: 'text-green-800 dark:text-green-100', icon: faShieldAlt },
  informational: { bg: 'bg-blue-100 dark:bg-blue-800', text: 'text-blue-800 dark:text-blue-100', icon: faShieldAlt }
};

const STATUS_CONFIG = {
  new: { bg: 'bg-blue-100 dark:bg-blue-800', text: 'text-blue-800 dark:text-blue-100' },
  investigating: { bg: 'bg-yellow-100 dark:bg-yellow-800', text: 'text-yellow-800 dark:text-yellow-100' },
  confirmed: { bg: 'bg-green-100 dark:bg-green-800', text: 'text-green-800 dark:text-green-100' },
  false_positive: { bg: 'bg-gray-100 dark:bg-gray-600', text: 'text-gray-800 dark:text-gray-100' },
  resolved: { bg: 'bg-purple-100 dark:bg-purple-800', text: 'text-purple-800 dark:text-purple-100' }
};

const FindingDetailModal = ({ finding, isOpen, onClose, projectId }) => {
  const { t, formatDate } = useLanguage();
  const { darkMode: isDarkMode } = useTheme();
  const navigate = useNavigate();

  if (!isOpen || !finding) return null;

  const title = finding.title || t('blueTeam.title');
  const description = finding.description || '';
  const severity = finding.severity || 'medium';
  const status = finding.status || 'new';
  const sevCfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.medium;
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;

  const analyst = finding.analyst || '';
  const detectionTimestamp = finding.detectionTimestamp;
  const activityTimestamp = finding.activityTimestamp;
  const detectionSource = finding.detectionSource || '';
  const detectionTool = finding.detectionTool || '';
  const suspectedTechnique = finding.suspectedTechnique;
  const affectedAssets = finding.affectedAssets || [];
  const iocs = finding.iocs || [];
  const alertInfo = finding.alertInfo || {};
  const analysisNotes = finding.analysisNotes || '';
  const relatedAction = finding.relatedAction;
  const tags = finding.tags || [];

  const resolvedProjectId = projectId || finding.project?._id || finding.project || '';

  const handleEdit = () => {
    if (resolvedProjectId && finding._id) {
      onClose();
      navigate(`/projects/${resolvedProjectId}/blue-team/findings/${finding._id}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-start justify-between p-6 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex-1 pr-4">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
            {suspectedTechnique && (
              <p className="text-sm text-blue-500 mt-1">
                <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                {suspectedTechnique.id}{suspectedTechnique.name ? ` - ${suspectedTechnique.name}` : ''}
                {suspectedTechnique.tactic && <span className={`ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>({suspectedTechnique.tactic})</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Severity badge */}
            <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1 ${sevCfg.bg} ${sevCfg.text}`}>
              <FontAwesomeIcon icon={sevCfg.icon} className="text-xs" />
              {t(`findings.${severity}`) !== `findings.${severity}` ? t(`findings.${severity}`) : severity.charAt(0).toUpperCase() + severity.slice(1)}
            </span>
            {/* Status badge */}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusCfg.bg} ${statusCfg.text}`}>
              {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            {/* Edit button */}
            {resolvedProjectId && finding._id && (
              <button
                onClick={handleEdit}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-blue-50 text-blue-600'}`}
                title={t('common.edit')}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            {/* Close button */}
            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Meta row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {analyst && (
              <div>
                <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FontAwesomeIcon icon={faUser} className="mr-1" />
                  {t('blueTeam.analyst') || 'Analyst'}
                </div>
                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{analyst}</div>
              </div>
            )}
            {detectionTimestamp && (
              <div>
                <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FontAwesomeIcon icon={faBell} className="mr-1" />
                  {t('findings.detectionTime') || 'Detection Time'}
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {format(new Date(detectionTimestamp), 'MMM d, yyyy HH:mm')}
                </div>
              </div>
            )}
            {activityTimestamp && (
              <div>
                <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FontAwesomeIcon icon={faClock} className="mr-1" />
                  {t('blueTeam.activityTime') || 'Activity Time'}
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {format(new Date(activityTimestamp), 'MMM d, yyyy HH:mm')}
                </div>
              </div>
            )}
            {detectionSource && (
              <div>
                <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FontAwesomeIcon icon={faSearch} className="mr-1" />
                  {t('blueTeam.source') || 'Source'}
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{detectionSource.replace(/_/g, ' ')}</div>
              </div>
            )}
          </div>

          {/* Detection tool */}
          {detectionTool && (
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
              <FontAwesomeIcon icon={faNetworkWired} className="mr-1" />
              {detectionTool}
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, i) => (
                <span key={i} className={`px-2 py-1 rounded-full text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          {description && (
            <div>
              <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-blue-500" />
                {t('common.description')}
              </h4>
              <p className={`text-sm leading-relaxed whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
            </div>
          )}

          {/* Alert Information */}
          {(alertInfo.alertId || alertInfo.ruleName || alertInfo.ruleId || alertInfo.confidence) && (
            <div>
              <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faBell} className="mr-2 text-yellow-500" />
                {t('blueTeam.alertInfo') || 'Alert Information'}
              </h4>
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="grid grid-cols-2 gap-3">
                  {alertInfo.alertId && (
                    <div>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Alert ID</span>
                      <div className={`text-sm font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{alertInfo.alertId}</div>
                    </div>
                  )}
                  {alertInfo.ruleName && (
                    <div>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rule Name</span>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{alertInfo.ruleName}</div>
                    </div>
                  )}
                  {alertInfo.ruleId && (
                    <div>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rule ID</span>
                      <div className={`text-sm font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{alertInfo.ruleId}</div>
                    </div>
                  )}
                  {alertInfo.confidence && (
                    <div>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Confidence</span>
                      <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{alertInfo.confidence}%</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Affected Assets */}
          {affectedAssets.length > 0 && (
            <div>
              <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faBullseye} className="mr-2 text-orange-500" />
                {t('blueTeam.affectedAssets') || 'Affected Assets'} ({affectedAssets.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {affectedAssets.map((asset, i) => (
                  <span
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-blue-50 text-blue-800'}`}
                  >
                    <span className="font-medium">[{asset.type}]</span> {asset.value}
                    {asset.description && <span className={`ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>- {asset.description}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* IOCs */}
          {iocs.length > 0 && (
            <div>
              <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faFingerprint} className="mr-2 text-red-500" />
                IOCs ({iocs.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {iocs.map((ioc, i) => (
                  <span
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-orange-50 text-orange-800'}`}
                  >
                    <span className="font-medium">[{ioc.type}]</span> {ioc.value}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Analysis Notes */}
          {analysisNotes && (
            <div>
              <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faStickyNote} className="mr-2 text-yellow-500" />
                {t('blueTeam.analysisNotes') || 'Analysis Notes'}
              </h4>
              <pre className={`text-sm whitespace-pre-wrap p-3 rounded-lg ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                {analysisNotes}
              </pre>
            </div>
          )}

          {/* Correlation Status */}
          {relatedAction && (
            <div>
              <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faLink} className="mr-2 text-purple-500" />
                {t('purpleTeam.correlated')}
              </h4>
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-purple-500 mr-2" />
                  <span className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                    {t('purpleTeam.linkedToAction') || 'Linked to red team action'}: {relatedAction.title || relatedAction._id || (typeof relatedAction === 'string' ? relatedAction : 'Unknown')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindingDetailModal;
